import { Button, LucideIcon } from '@skin-seoul/design-system';
import { formatReviewPostedOn } from '@skin-seoul/service';
import type { Review, ReviewImage } from '..';
import { StarIcon } from '../assets/icons/StarIcon';
import { ReviewCardImageCarousel } from './ReviewCardImageCarousel';
import { RepeatPurchaserBadge, VerifiedPurchaserBadge } from './PurchaseBadges';

interface MyReviewCardProps {
  review: Review;
  onImageClick: (image: ReviewImage, index: number) => void;
  onViewMore?: (review: Review) => void;
  onImageIndexChange?: (review: Review, toIndex: number) => void;
  onEdit?: (review: Review) => void;
}

/**
 * The author's own review is its own card rather than a flag on `ReviewCard`: it leads with an
 * ownership header and an Edit action, and moves the date to the bottom as `Posted on`.
 */
export const MyReviewCard = ({ review, onImageClick, onViewMore, onImageIndexChange, onEdit }: MyReviewCardProps) => {
  const hasMedia = Boolean(review.images && review.images.length > 0);
  const hasPurchaseBadge = Boolean(review.verifiedPurchaser || review.repeatPurchaser);
  const postedOn = formatReviewPostedOn(review.createdAt);

  return (
    <div className="flex flex-col gap-4 w-full rounded-[8px] bg-neutral-50 p-4" data-testid="my-review-card">
      <div className="flex flex-row items-start justify-between gap-2 w-full">
        <div className="flex flex-col items-start gap-1">
          <p className="text-paragraph-large font-semibold text-neutral-800">Your Review</p>
          <div className="flex flex-row items-center gap-1">
            <LucideIcon name="Info" width="16px" height="16px" className="text-neutral-500 shrink-0" />
            <p className="text-label-small font-normal text-neutral-500">You can edit your review at any time.</p>
          </div>
        </div>
        {/* The edit form is a separate deliverable; rendering a dead Edit button would be worse
            than no button, so the action only appears once a handler is supplied. */}
        {onEdit && (
          <Button
            variant="plain"
            className="max-w-[fit-content] max-h-[fit-content] px-0 text-primary-400 text-label-small"
            onClick={() => onEdit(review)}
          >
            <LucideIcon name="SquarePen" width="16px" height="16px" className="text-primary-400" />
            Edit
          </Button>
        )}
      </div>

      {hasMedia && (
        <ReviewCardImageCarousel
          images={review.images || []}
          width="full"
          height="298px"
          onImageClick={onImageClick}
          onIndexChange={(toIndex) => onImageIndexChange?.(review, toIndex)}
        />
      )}

      {/* Both badges sit by the rating here. The owner's card has no author box, which is where
          Repeat Purchaser lands on everyone else's review. */}
      <div className="flex flex-row gap-4 items-center">
        <div className="flex flex-row gap-1 items-center shrink-0">
          <p className="text-heading-h5 font-semibold text-primary-400">{review.rating}</p>
          <StarIcon width={20} height={20} />
        </div>
        {hasPurchaseBadge && (
          <div className="flex flex-row gap-2 items-center">
            {review.verifiedPurchaser && <VerifiedPurchaserBadge />}
            {review.repeatPurchaser && <RepeatPurchaserBadge />}
          </div>
        )}
      </div>

      <div className="flex flex-col items-start gap-2 w-full">
        <p className="text-paragraph-large font-semibold text-neutral-800">{review.title}</p>
        <p className="text-paragraph-small font-normal text-neutral-500 line-clamp-3">{review.content}</p>
        <Button
          className="max-w-[fit-content] max-h-[fit-content] text-primary-400 text-label-small px-0"
          variant="plain"
          onClick={() => onViewMore?.(review)}
        >
          View More
        </Button>
      </div>

      {postedOn && <p className="text-label-small font-normal text-neutral-400">Posted on {postedOn}</p>}
    </div>
  );
};

export default MyReviewCard;
